import { Dispatch, SetStateAction, useState } from 'react';

import { DownloadIcon, EyeIcon } from '../../../../assets/svg';
import { ETPayButton } from '../../../../components/button';
import { DateAndTime } from '../../../../components/date-and-time';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { Status } from '../../../../components/status';
import { ETPayTable } from '../../../../components/table';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { RefundsConsultData } from '../../../../models/refund';
import {
	ColumnInterface,
	Filters,
	FilterStatus,
	Pagination,
	RowInterface,
} from '../../../../models/table';
import { RefundsServices } from '../../../../services/refund';
import { validatePermission } from '../../../../utils/common';
import { errorFormatter } from '../../../../utils/errors';
import { fillStatesWithZeros } from '../../../../utils/states';
import {
	AmountContainer,
	Content,
	DetailsContainer,
	ExportModalContent,
	ExportModalDescription,
	ExportModalTitle,
	ItemData,
	Section,
	StatusContainer,
	TextSub,
	TextTitle,
	Title,
	Value,
	VoucherButton,
	VoucherContainer,
} from './styles';

interface RefundsTableProps {
	data: RefundsConsultData[];
	isLoading?: boolean;
	filters: Filters;
	onFilterChange: Dispatch<SetStateAction<Filters>>;
	pagination: Pagination;
	today?: Date;
	pastDate?: Date;
	setPagination: Dispatch<SetStateAction<Pagination>>;
	totalResults?: number;
	stateList: FilterStatus[];
	voucherPdf?: boolean;
}

interface ModalProps {
	title: string;
	message: string;
	loader: boolean;
	show: boolean;
	url: string;
}

const defaultModal: ModalProps = {
	title: 'Exportar',
	message: 'Cargando la información',
	loader: false,
	show: false,
	url: '',
};

const RefundsTable = ({
	data,
	isLoading,
	today,
	pastDate,
	filters,
	onFilterChange,
	pagination,
	setPagination,
	totalResults,
	stateList,
	voucherPdf,
}: RefundsTableProps) => {
	const { merchantSelected } = useMerchant();
	const [modal, setModal] = useState<ModalProps>(defaultModal);
	const { handleShowAlert } = useFloatingAlert();

	const getColumns = () => {
		const columns: ColumnInterface<RefundsConsultData>[] = [
			{
				sortField: true,
				title: 'Concepto',
				propertyName: 'paymentConcept',
				cellRender: ({ paymentConcept }) => <>{paymentConcept}</>,
			},
			{
				sortField: true,
				title: 'Monto devuelto',
				align: 'right',
				propertyName: 'payoutAmount',
				cellRender: ({ payoutAmount }) => <AmountContainer>{payoutAmount}</AmountContainer>,
			},
			{
				sortField: true,
				title: 'Cuenta',
				propertyName: 'beneficiaryAccount',
				cellRender: ({ targetBank, beneficiaryAccount }) => (
					<ItemData>
						<TextTitle>{beneficiaryAccount}</TextTitle>
						<TextSub>{targetBank}</TextSub>
					</ItemData>
				),
			},
			{
				title: 'Estatus',
				align: 'center',
				propertyName: 'status',
				cellRender: ({ finalstatus }) => (
					<StatusContainer>
						<Status status={finalstatus} borderFull />
					</StatusContainer>
				),
			},
			{
				sortField: true,
				title: 'Fecha de solicitud',
				propertyName: 'createdatDate',
				cellRender: ({ createdatDate, createdatTime }) => (
					<DateAndTime date={createdatDate} time={createdatTime} />
				),
			},
			{
				title: 'Fecha de devolución',
				propertyName: 'payoutDate',
				cellRender: ({ payoutDate, payoutTime }) => (
					<DateAndTime date={payoutDate} time={payoutTime} />
				),
			},
		];
		if (voucherPdf) {
			columns.push({
				title: 'Comprobante',
				align: 'center',
				propertyName: 'voucher',
				cellRender: ({ trackingKey, createdatDate, finalstatus }) => (
					<>
						{finalstatus == 'success' && (
							<VoucherContainer>
								<VoucherButton onClick={() => handleExportPdf(trackingKey, createdatDate)}>
									<DownloadIcon color="#646174" />
								</VoucherButton>
							</VoucherContainer>
						)}
					</>
				),
			});
		}
		if (merchantSelected?.provider == 'stp' || merchantSelected?.provider == 'tesored' || merchantSelected?.provider == null) {
			columns.push({
				title: 'CEP',
				align: 'center',
				hide: merchantSelected?.country !== 'MX',
				propertyName: 'urlCEP',
				cellRender: ({ urlCEP }) => (
					<VoucherContainer>
						{urlCEP && (
							<VoucherButton onClick={() => window.open(urlCEP)}>
								<EyeIcon />
							</VoucherButton>
						)}
					</VoucherContainer>
				),
			});
		}
		return columns;
	};

	const columns = getColumns();

	const detailSection: RowInterface<RefundsConsultData> = (data) => {
		const isError = data.finalstatus === 'error';

		return (
			<DetailsContainer>
				<Section>
					<Content>
						<Title>Clave de rastreo</Title>
						<Value>{data.trackingKey}</Value>
					</Content>
					<Content>
						<Title>Nombre del beneficiario</Title>
						<Value>{data.beneficiaryName}</Value>
					</Content>
					<Content>
						<Title>Correo electrónico del beneficiario</Title>
						<Value>{data.email}</Value>
					</Content>

					{isError && (
						<Content>
							<Title>Detalle del error</Title>
							<Value>{data.detail}</Value>
						</Content>
					)}
					{!isError && (
						<Content>
							<Title>Referencia del pago</Title>
							<Value>{data.reference}</Value>
						</Content>
					)}
				</Section>

				<Section>
					<Content>
						<Title>Payment ID</Title>
						<Value>{data.paymentToken}</Value>
					</Content>
				</Section>
			</DetailsContainer>
		);
	};

	const handleExportPdf = async (token: string, date: string) => {
		setModal({
			...modal,
			loader: true,
			show: true,
			title: 'Descargar',
			message: 'Generando comprobante',
		});

		try {
			const response = await RefundsServices.getExportPdf({
				merchantCode: merchantSelected?.code as string,
				token: token,
				date: date,
				origin: 'payouts',
			});

			if (response.metadata.pdf)
				setModal({
					...modal,
					loader: false,
					show: false,
					title: 'Exportar',
					message: 'Cargando la información',
				});
			window.open(response.metadata.pdf, '_blank');
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: errorFormatter(e.code),
			});

			setModal({
				...modal,
				loader: false,
				show: false,
				title: 'Exportar',
				message: 'Cargando la información',
			});
		}
	};

	const handleCloseModal = () => {
		setModal(defaultModal);
	};

	const handleDownloadData = () => {
		if (modal.url) window.location.assign(modal.url);
		setModal(defaultModal);
	};

	const handleExportAction = async () => {
		setModal({ ...modal, loader: true, show: true });

		try {
			const response = await RefundsServices.getExportData({
				merchantCode: merchantSelected?.code as string,
				dateRange: {
					from: filters?.dateRange?.from || pastDate,
					to: filters?.dateRange?.to || today,
				},
				search: filters?.search,
				stateSelected: filters?.stateSelected,
			});

			if (response.url) setModal({ ...modal, loader: false, show: true, url: response.url });
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: errorFormatter(e.code),
			});
		}
	};

	const consultRefundStates = ['liquidated', 'pending', 'error'];

	return (
		<>
			{modal.show && (
				<Modal
					title={modal.title}
					show={modal.show}
					onClose={handleCloseModal}
					allowCloseModal={!modal.loader}
				>
					{modal.loader ? (
						<Spinner message={modal.message} />
					) : (
						<ExportModalContent>
							<ExportModalTitle>Tu reporte está listo</ExportModalTitle>

							<ExportModalDescription>
								Hemos terminado de recopilar los datos de tu reporte. Haz clic en el botón para
								descargarlo.
							</ExportModalDescription>
							<ETPayButton
								title="Descargar reporte"
								variant="effect"
								onClick={handleDownloadData}
							/>
						</ExportModalContent>
					)}
				</Modal>
			)}

			<ETPayTable<RefundsConsultData>
				title="Todas las devoluciones"
				data={data}
				isLoading={!!isLoading}
				columns={columns}
				row={detailSection}
				hasFilters
				hasDateFilter
				allowExport={validatePermission('REFUNDS_EXPORT')}
				exportAction={handleExportAction}
				filters={filters}
				onFilterChange={onFilterChange}
				hasSearch
				hasPagination
				today={today}
				pastDate={pastDate}
				totalResults={totalResults}
				pagination={pagination}
				emptyMessage="No hay información disponible en el periodo seleccionado"
				setPagination={setPagination}
				stateList={fillStatesWithZeros({
					states: consultRefundStates,
					stateList,
				})}
				statusContent={[
					['liquidated', 'Liquidados'],
					['pending', 'En proceso'],
					['error', 'Fallidos'],
				]}
			/>
		</>
	);
};

export { RefundsTable };
